import React from "react";
import { styled } from "@mui/material";
import { createSafeContext } from "~/contexts";
import type { ContainerNode, LayoutNode, PanelNode } from "./api";
import { SplitOrientation } from "./constants";
import { usePanelLayoutContext } from "./provider";
import { resizeNode } from "./reducer";
import { useSplitNodes } from "./use-split-nodes";

const rootStyles = {
  height: "100%",
  width: "100%",
  position: "relative",
  boxSizing: "border-box",
} as const;

const PanelRoot = styled("div")(rootStyles);

const ClassNames = {
  Splitter: "container-splitter",
} as const;

const ContainerRoot = styled("div")({
  ...rootStyles,
  display: "flex",
  overflow: "hidden",
  [`& > .${ClassNames.Splitter}`]: {
    backgroundColor: "darkgrey",
    flex: "none",
  },
  [`&[data-orientation="${SplitOrientation.Horizontal}"]`]: {
    flexDirection: "column",
    [`& > .${ClassNames.Splitter}`]: {
      width: "100%",
      height: "3px",
      cursor: "ns-resize",
    },
  },
  [`&[data-orientation="${SplitOrientation.Vertical}"]`]: {
    flexDirection: "row",
    [`& > .${ClassNames.Splitter}`]: {
      width: "3px",
      height: "100%",
      cursor: "ew-resize",
    },
  },
});

export function Panels({ element }: { element: React.ReactNode }) {
  const { layout: rootNode } = usePanelLayoutContext();

  return <Node node={rootNode} element={element} />;
}

function Node({
  node,
  element,
}: {
  node: LayoutNode;
  element: React.ReactNode;
}) {
  if (node.type === "container") {
    return <Container node={node} element={element} />;
  } else {
    return <Panel node={node} element={element} />;
  }
}

function Container({
  node,
  element,
}: {
  node: ContainerNode;
  element: React.ReactNode;
}) {
  const { dispatch } = usePanelLayoutContext();

  const splitPanels = useSplitNodes({
    orientation: node.orientation,
    firstNodeFlex: node.firstChild.flex,
    secondNodeFlex: node.secondChild.flex,
    onResizeStop(firstNodeFlex, secondNodeFlex) {
      dispatch(resizeNode({ nodeId: node.firstChild.id, flex: firstNodeFlex }));
      dispatch(
        resizeNode({ nodeId: node.secondChild.id, flex: secondNodeFlex }),
      );
    },
  });

  return (
    <ContainerRoot {...splitPanels.containerProps}>
      <div {...splitPanels.firstNodeProps}>
        <Node node={node.firstChild} element={element} />
      </div>
      <div className={ClassNames.Splitter} {...splitPanels.splitterProps} />
      <div {...splitPanels.secondNodeProps}>
        <Node node={node.secondChild} element={element} />
      </div>
    </ContainerRoot>
  );
}

export const [usePanelContext, PanelContext] =
  createSafeContext<PanelNode>("Panel");

function Panel({
  node,
  element,
}: {
  node: PanelNode;
  element: React.ReactNode;
}) {
  return (
    <PanelContext.Provider value={node}>
      <PanelRoot>{element}</PanelRoot>
    </PanelContext.Provider>
  );
}
